import { isKeyHotkey } from 'is-hotkey';
import { Range, Transforms, Text, Editor } from 'slate';

import { replaceWithMark, revertMark } from './Mark';
import { markRegexp } from './Utils';
import type { EditorPlugin, MarkdownData } from '../../types/aha-editor';
import History from '../../collaboration/History';
import type AhaEditor from '../../AhaEditor';

const isBackspace = isKeyHotkey('backspace');
const isShiftTab = isKeyHotkey('shift+tab');
const isEnter = isKeyHotkey('enter');

const TRIGGER_CHARACTERS = [' ', '.', ',', '!', '?'];

// TODO::Plugins Move this to a class instead of using globals
let previous: MarkdownData | null = null;

function getTextUpToCursor(editor: Editor): string | undefined {
  const { selection } = editor;

  if (!selection || !Range.isCollapsed(selection)) return;

  const currentBlockEntry = Editor.parent(editor, selection);
  if (!currentBlockEntry) return;

  const blockStart = Editor.start(editor, currentBlockEntry[1]);
  const cursorPoint = selection.anchor;

  const rangeUpToCursor: Range = {
    anchor: blockStart,
    focus: cursorPoint,
  };

  return Editor.string(editor, rangeUpToCursor);
}

export function handleConvert(
  event: KeyboardEvent,
  ahaEditor: AhaEditor,
  options
) {
  options = {
    test: () => true,
    ignoreSameCharacterString: true,
    isEnter: false,
    triggerCharacters: TRIGGER_CHARACTERS,
    ignoreUncaptured: true,
    convert: replaceWithMark,
    ...options,
  };

  const { triggerCharacters, shortcut, convert, test, type } = options;
  const { editor } = ahaEditor;
  const { selection } = editor;
  if (!selection) return;

  const isCollapsed = Range.isCollapsed(selection);
  const start = Range.start(selection);
  let { key } = event;

  if (!triggerCharacters.includes(key)) {
    key = '';
  }

  if (!isCollapsed || !start.path || !test(editor)) return;

  // TODO::Markdown This only grabs one node, the markdown text could
  // span multiple nodes, e.g. if a portion of the text has other marks.
  const nodeEntry = Editor.node(editor, start.path);
  if (!nodeEntry) return;

  const [node] = nodeEntry;

  const matchText = getTextUpToCursor(editor);
  if (!matchText) return;

  let trigger =
    shortcut instanceof RegExp ? shortcut : new RegExp(`^${shortcut}$`, 'i');
  if (type === 'mark') {
    trigger = markRegexp(shortcut);
  }
  const matchData = matchText.match(trigger);

  if (!matchData) return;

  if (options.ignoreSameCharacterString) {
    const baseText = matchData[0].trim();
    const isAllSameChar = baseText
      .trim()
      .split('')
      .every((c) => c === baseText[0]);

    if (isAllSameChar) return;
  }

  event.preventDefault();

  const captureText = options.ignoreUncaptured ? matchData[1] : matchData[0];
  const markdownText = `${captureText}${key}`;

  const revert = ['block', 'inline'].includes(type)
    ? options.revert
    : revertMark;

  if (!Text.isText(node)) return;
  const { text, ...activeMarks } = node;
  const markdownOptions = {
    ...options,
    revert,
    path: start.path,
    text: markdownText,
    matchData,
    triggerKey: key,
    activeMarks,
  };

  if (key.length > 0) {
    Editor.insertText(editor, key);
  }

  History.withoutUndoMerging(editor, (editor) => {
    Transforms.delete(editor, {
      distance: markdownText.length,
      unit: 'character',
      reverse: true,
    });
  });

  convert(editor, markdownOptions);

  if (isEnter(event)) {
    markdownOptions.startPath = start.path;
  }
  previous = markdownOptions;

  return true;
}

const clearPrevious = (editor, op, next) => {
  if (previous !== null) {
    previous = null;
  }

  next(editor, op);
};

const convert = (event, ahaEditor) => {
  const { markdown } = ahaEditor;
  for (const markdownElement of markdown) {
    let result = false;
    const triggerCharacters =
      markdownElement.triggerCharacters ?? TRIGGER_CHARACTERS;

    if (triggerCharacters.includes(event.key)) {
      result = !!handleConvert(event, ahaEditor, {
        ...markdownElement,
        triggerCharacters,
      });
    }

    if (result) return result;
  }
};

const onEnter = (event: KeyboardEvent, ahaEditor: AhaEditor): undefined => {
  if (!isEnter(event)) return;

  const { editor, markdown } = ahaEditor;

  let result;

  for (const markdownElement of markdown) {
    if (markdownElement.allowEnter) {
      result = handleConvert(event, ahaEditor, { ...markdownElement });
      if (result) {
        Transforms.splitNodes(editor, { always: true });

        if (markdownElement.afterEnter) {
          markdownElement.afterEnter(editor);
        }
      }
      // return result;
    }
  }
};

const revert = (event: KeyboardEvent, ahaEditor: AhaEditor): undefined => {
  if (previous === null) return;

  if (!isBackspace(event) && (!isShiftTab(event) || !previous.shiftTabRevert)) {
    return;
  }

  const { revert, path, text, startPath } = previous;
  const { editor } = ahaEditor;

  const previousEntry = Editor.node(editor, path);
  if (!previousEntry) return;

  event.preventDefault();

  if (startPath) {
    const { selection } = editor;
    if (!selection) return;

    const node = Editor.node(editor, startPath);
    if (!node) return;

    Transforms.move(editor, { edge: 'end' });
    revert(editor, previousEntry, previous);
    Transforms.insertText(editor, text);
    Transforms.setSelection(editor, selection);
  } else {
    revert(editor, previousEntry, previous);
    Transforms.insertText(editor, text);
  }
};

const Markdown: EditorPlugin = {
  name: 'Markdown plugin',
  handlers: {
    onKeyDown: [convert, revert, onEnter],
  },
  operationFilter: clearPrevious,
};

export default Markdown;
